import axios from 'axios';

const instance = axios.create({
  baseURL: 'https://selina-renee-db-server.onrender.com',
  //baseURL: 'http://localhost:3001',
});

// Add a request interceptor to include the JWT token
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token'); // Retrieve token from localStorage
    if (token) {
      config.headers.Authorization = `Bearer ${token}`; // Set the token in Authorization header
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        window.location.href = '/login'; // Redirect if no refresh token
        return Promise.reject(error);
      }

      try {
        const response = await instance.post('/api/refresh-token', { refreshToken });
        const { token, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem('token', token); // Update tokens
        localStorage.setItem('refreshToken', newRefreshToken);

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return instance(originalRequest);
      } catch (err) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login'; // Redirect if refresh fails
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);


const graphInstance = axios.create({
  baseURL: 'https://graph.microsoft.com/v1.0',
});

export const OrderService = {

  // Login function
  login(username, password) {
    return instance.post('/api/login', { username, password })
      .then(response => response.data)
      .catch(error => {
        console.error('Error during login:', error);
        throw error;
      });
  },


  getOrders() {
    return instance.get('/api/orders')
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching orders:', error);
        throw error;
      });
  },

  getOrdersByDateRange(startDate, endDate) {
    return instance.get('/api/orders', {
      params: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      }
    })
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching orders by date range:', error);
        throw error;
      });
  },


  getOrderById(id) {
    return instance.get(`/api/orders/${id}`)
      .then(response => response.data)
      .catch(error => {
        console.error(`Error fetching order with id ${id}:`, error);
        throw error;
      });
  },

  getEmployees() {
    return instance.get('/api/employees')
      .then(response => response.data) // Extract the employee array from the response data
      .catch(error => {
        console.error('Error fetching employees:', error);
        throw error;
      });
  },

  getSquareCustomers() {
    return instance.get('/api/squarecustomers')
      .then(response => response.data) // Extract the employee array from the response data
      .catch(error => {
        console.error('Error fetching square customers:', error);
        throw error;
      });
  },


  getSquareCustomersByFirstName(firstName) {
    return instance.get(`/api/squarecustomers/firstname?q=${firstName}`)
      .then(response => response.data) // Extract the employee array from the response data
      .catch(error => {
        console.error('Error fetching square customers by first name:', error);
        throw error;
      });
  },

  getSquareCustomersByLastName(lastName) {
    return instance.get(`/api/squarecustomers/lastname?q=${lastName}`)
      .then(response => response.data) // Extract the employee array from the response data
      .catch(error => {
        console.error('Error fetching square customers by last name:', error);
        throw error;
      });
  },



  getProductProperties() {
    return instance.get('/api/product_properties')
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching product properties:', error);
        throw error;
      });
  },

  // Add this to the existing OrderService.js

  // updateProductProperties(id, productProperties) {
  //   return instance.put(`/api/product_properties/${id}`, productProperties)
  //     .then(response => response.data)
  //     .catch(error => {
  //       console.error('Error updating product properties:', error);
  //       throw error;
  //     });
  // },

   updateProductProperties(id, payload) {
    return instance.put(`/api/product_properties/${id}`, payload)
      .then(res => res.data)
      .catch(err => {
        console.error('Error updating product properties:', err);
        throw err;
      });
  },

  getOrderProperties() {
    return instance.get('/api/order_properties')
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching order properties:', error);
        throw error;
      });
  },

  getRoles() {
    return instance.get('/api/roles')
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching roles:', error);
        throw error;
      });
  },

  updateOrder(id, data) {
    // console.log('Updating order:', id, data);
    return instance.patch(`/api/orders/${id}`, data)
      .then(response => {
        // console.log('Update Order Response:', response.data);
        return { ...response.data, _id: id };
      })
      .catch(error => {
        console.error('Error updating order:', error);
        throw error;
      });
  },

  createOrder(data) {
    //console.log('Creating order:', data);
    return instance.post(`/api/orders`, data)
      .then(response => {
        //console.log('Create Order Response:', response.data);
        return { ...response.data, _id: response.data._id };
      })
      .catch(error => {
        console.error('Error creating order:', error);
        throw error;
      });
  },

  deleteOrder: (orderId) => {
    return instance.delete(`/api/orders/${orderId}`)
      .then(response => response.data)
      .catch(error => {
        console.error(`Error deleting order with id ${orderId}:`, error);
        throw error;
      });
  },
  
  // OrderService.js — replace existing uploadImage
uploadImage(file, orderDueDate, customerName, onProgress) {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('orderDueDate', orderDueDate);
  formData.append('customerName', customerName);

  return instance.post('/api/upload-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percent);
      }
    },
  })
    .then(response => response.data)
    .catch(error => {
      console.error('Error uploading image:', error);
      throw error;
    });
},


  deleteImageFromCloudinary: async (publicId) => {
    try {
      const response = await instance.delete('/api/cloudinary/delete', {
        data: { publicId }, // Pass publicId as part of the request body
      });
      //console.log('Image deleted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting image from Cloudinary:', error);
      throw error;
    }
  },

  // getOneDriveDirectory(accessToken) {
  //   graphInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  //   return graphInstance.get('/me/drive/root/children')
  //     .then(response => {deleteOrder
  //       console.log('OneDrive Directory Data:', response.data);
  //       return response.data;
  //     })
  //     .catch(error => {
  //       console.error('Error fetching OneDrive directory:', error);
  //       throw error;
  //     });
  // },

  async getSquareInvoices() {
    return instance.get('/api/square/invoices')
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching Square invoices:', error);
        throw error;
      });
  },

  async getSquareInvoice(invoiceId) {
    return instance.get(`/api/square/invoices/${invoiceId}`)
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching Square invoice:', error);
        throw error;
      });
  },

  async searchSquareInvoicesBySquareCustomerID(squareCustomerID) {
    return instance.get(`/api/square/invoices/search/squareCustomerID/${squareCustomerID}`)
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching Square invoice:', error);
        throw error;
      });
  },

  async getSquareCustomer(customerId) {
    return instance.get(`/api/square/customers/id/${customerId}`)
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching Square customer:', error);
        throw error;
      });
  },

  async searchSquareCustomerEmail(email) {
    return instance.get(`/api/square/customers/searchEmail/${email}`)
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching Square customer:', error);
        throw error;
      });
  },
  async searchSquareCustomerPhone(phone) {
    return instance.get(`/api/square/customers/searchPhone/${phone}`)
      .then(response => response.data)
      .catch(error => {
        console.error('Error fetching Square customer:', error);
        throw error;
      });
  },

};

export default OrderService;